'use client';

import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment, useEffect } from 'react';
import { useElevenLabsToken } from '@components/templates/audio-interview/hooks/useElevenLabsToken';
import VoiceAgentCard from './VoiceAgentCard';

interface VoiceAgentModalProps {
  agentId: string;
  locale: Locale;
}

/**
 * VoiceAgentModal
 *
 * Beautiful modal with Headless UI animations:
 * - Backdrop blur effect
 * - Smooth scale transitions
 * - Rounded corners with shadow
 * - Fetches token and passes to card
 */
export default NiceModal.create(({ agentId, locale }: VoiceAgentModalProps) => {
  const modal = useModal();
  const {
    conversationToken, isLoading, error, fetchToken,
  } = useElevenLabsToken(locale, agentId);

  // Fetch token when modal opens
  useEffect(() => {
    if (modal.visible) {
      void fetchToken();
    }
  }, [modal.visible, fetchToken]);

  return (
    <Transition appear show={modal.visible} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={() => { void modal.hide(); }}>
        {/* Backdrop with blur */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
        </Transition.Child>

        {/* Modal container */}
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel className="w-full max-w-sm rounded-3xl bg-neutral-50 shadow-2xl">
              {/* Always render VoiceAgentCard - it handles all states */}
              <VoiceAgentCard
                conversationToken={conversationToken}
                isLoadingToken={isLoading}
                tokenError={error}
                locale={locale}
                onClose={() => { void modal.hide(); }}
              />
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
});
